import React, { useEffect } from "react";
import { KeywordWithMetadata } from "../types";
import TypeaheadSelect, { ClusterOption } from "./TypeaheadSelect";
import "./ControlPanel.css";
import { FormControl, MenuItem, Select, Tooltip } from "@mui/material";

interface ControlPanelProps {
  anchorKeywords: ClusterOption[];
  selectedAnchor: string;
  setSelectedAnchor: (anchor: string) => void;
  useDynamicAxes: boolean;
  setUseDynamicAxes: (useDynamic: boolean) => void;
  showLabels: boolean;
  setShowLabels: (showLabels: boolean) => void;
  colorMode: "trend" | "similarity";
  setColorMode: (mode: "trend" | "similarity") => void;
  trendPeriod: "week" | "month" | "life";
  setTrendPeriod: (period: "week" | "month" | "life") => void;
  keywordData: KeywordWithMetadata[];
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  anchorKeywords,
  selectedAnchor,
  setSelectedAnchor,
  useDynamicAxes,
  setUseDynamicAxes,
  showLabels,
  setShowLabels,
  colorMode,
  setColorMode,
  trendPeriod,
  setTrendPeriod,
  keywordData,
}) => {
  // A period has "no data" only when ALL keywords have null (no snapshot for comparison).
  // A value of 0 means "rank unchanged" — that IS valid data.
  const hasNoDataForPeriod = (
    period: "week" | "month" | "life"
  ): boolean => {
    if (keywordData.length === 0) return true;
    return keywordData.every((keyword) => keyword.trend[period] === null);
  };

  const trendPeriodOptions = [
    { value: "week", label: "Week", disabled: hasNoDataForPeriod("week") },
    { value: "month", label: "Month", disabled: hasNoDataForPeriod("month") },
    { value: "life", label: "Lifetime", disabled: hasNoDataForPeriod("life") },
  ];

  // Auto-switch to a period with data if current period is disabled
  useEffect(() => {
    const currentOptionDisabled = trendPeriodOptions.find(
      (option) => option.value === trendPeriod
    )?.disabled;

    if (currentOptionDisabled) {
      // Find the first period with data, preferring longer periods
      const availableOption = trendPeriodOptions
        .slice()
        .reverse() // Start from lifetime and go backwards
        .find((option) => !option.disabled);

      if (availableOption) {
        setTrendPeriod(
          availableOption.value as "week" | "month" | "life"
        );
      }
    }
  }, [keywordData, trendPeriod, setTrendPeriod, trendPeriodOptions]);
  return (
    <div className="flex flex-col gap-y-6">
      <TypeaheadSelect
        id="anchor-select"
        options={anchorKeywords}
        value={selectedAnchor}
        onChange={setSelectedAnchor}
        placeholder="Search..."
      />

      <div>
        <div className="label mb-3">
          Visualization Controls
        </div>

        <div className="">
          <label className="checkbox-container at-dashboard">
            <input
              id="dynamic-axes-checkbox"
              type="checkbox"
              checked={useDynamicAxes}
              onChange={(e) => setUseDynamicAxes(e.target.checked)}
            />
            <span className="checkmark"></span>
            <div className="flex items-center gap-1.5">
              <div className="font-medium leading-[1]">Use Dynamic Axes</div>
              <Tooltip 
                title="Auto-scale to data range + 10%" 
                placement="right"
              >
                <div className="h-5 aspect-square bg-[#e8d0ff] rounded-full flex items-center justify-center text-xs text-my-purple font-semibold">i</div>
              </Tooltip>
            </div>
          </label>
        </div>

        <div className="">
          <label className="checkbox-container at-dashboard">
            <input
              id="show-labels-checkbox"
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
            />
            <span className="checkmark"></span>
            <div className="font-medium leading-[1]">Show Keyword Labels</div>
          </label>
        </div>
      </div>

      <FormControl fullWidth className="flex flex-col gap-y-1.5">
        <label 
          htmlFor="color-mode-select" 
          id="color-mode-label" 
          className="label"
        >
          Bubble Color Mode:
        </label>

        <Select
          labelId="color-mode-label"
          id="color-mode-select"
          value={colorMode}
          onChange={(e) => setColorMode(e.target.value as "trend" | "similarity")}
        >
          <MenuItem value="trend">Trend</MenuItem>
          <MenuItem value="similarity">Similarity</MenuItem>
        </Select>
      </FormControl>

      {colorMode === "trend" && (
        <FormControl fullWidth className="flex flex-col gap-y-1.5">
          <label 
            htmlFor="trend-period-select" 
            className="label"
            id="trend-period-label"
          >
            Trend Period:
          </label>

          <Select
            id="trend-period-select"
            labelId="trend-period-label"
            value={trendPeriod}
            onChange={(e) =>
              setTrendPeriod(
                e.target.value as "week" | "month" | "life"
              )
            }
          >
            {trendPeriodOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
                {option.disabled ? " (No data)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </div>
  );
};

export default ControlPanel;
